import { forwardRef, useEffect, useMemo, useState, type CSSProperties } from "react";
import type { DeepPartial } from "@ortex-charts/core";
import {
    CategoryChart,
    Heatmap,
    PieChart,
    Sparkline,
    Treemap,
    XYChart,
    type CategoryChartHandle,
    type CategoryChartOptions,
    type HeatmapHandle,
    type HeatmapOptions,
    type PieChartHandle,
    type PieChartOptions,
    SankeyChart,
    type SparklineHandle,
    type SparklineOptions,
    type TreemapHandle,
    type TreemapOptions,
    type XYChartHandle,
    type XYChartOptions,
} from "@ortex-charts/react";
import type { SankeyChart as SankeyChartHandle, SankeyChartOptions } from "@ortex-charts/viz";
import type { SparklineData } from "@ortex-charts/lite";
import { ensureOrtexCharts } from "@/lib/ortex-charts/setup";
import { useOrtexChartTheme } from "@/lib/ortex-charts/theme";

/**
 * The non-timeline charts, each the library's React binding with the app
 * theme applied and the licence set before the first paint. Options are
 * whatever the library component takes; `theme` is filled in here.
 */
function useReady(): boolean {
    const [ready, setReady] = useState(false);
    useEffect(() => {
        let alive = true;
        void ensureOrtexCharts().then(() => alive && setReady(true));
        return () => {
            alive = false;
        };
    }, []);
    return ready;
}

interface WrapProps<O> {
    options: DeepPartial<O>;
    height?: number | string;
    className?: string;
    style?: CSSProperties;
}

function sized(height: number | string | undefined, style: CSSProperties | undefined): CSSProperties {
    return { width: "100%", height: height ?? "100%", ...style };
}

export const CategoryBarChart = forwardRef<CategoryChartHandle | null, WrapProps<CategoryChartOptions>>(function CategoryBarChart({ options, height, className, style }, ref) {
    const theme = useOrtexChartTheme();
    const ready = useReady();
    const merged = useMemo(() => ({ ...options, theme }), [options, theme]);
    if (!ready) return <div className={className} style={sized(height, style)} />;
    return (
        <div data-ortex-chart="" className={className} style={sized(height, style)}>
            <CategoryChart ref={ref} options={merged} style={{ width: "100%", height: "100%" }} />
        </div>
    );
});

export const XYPlot = forwardRef<XYChartHandle | null, WrapProps<XYChartOptions>>(function XYPlot({ options, height, className, style }, ref) {
    const theme = useOrtexChartTheme();
    const ready = useReady();
    const merged = useMemo(() => ({ ...options, theme }), [options, theme]);
    if (!ready) return <div className={className} style={sized(height, style)} />;
    return (
        <div data-ortex-chart="" className={className} style={sized(height, style)}>
            <XYChart ref={ref} options={merged} style={{ width: "100%", height: "100%" }} />
        </div>
    );
});

export const PieView = forwardRef<PieChartHandle | null, WrapProps<PieChartOptions>>(function PieView({ options, height, className, style }, ref) {
    const theme = useOrtexChartTheme();
    const ready = useReady();
    const merged = useMemo(() => ({ ...options, theme }), [options, theme]);
    if (!ready) return <div className={className} style={sized(height, style)} />;
    return (
        <div data-ortex-chart="" className={className} style={sized(height, style)}>
            <PieChart ref={ref} options={merged} style={{ width: "100%", height: "100%" }} />
        </div>
    );
});

export const SankeyView = forwardRef<SankeyChartHandle | null, WrapProps<SankeyChartOptions>>(function SankeyView({ options, height, className, style }, ref) {
    const theme = useOrtexChartTheme();
    const ready = useReady();
    const merged = useMemo(() => ({ ...options, theme }), [options, theme]);
    if (!ready) return <div className={className} style={sized(height, style)} />;
    return (
        <div data-ortex-chart="" className={className} style={sized(height, style)}>
            <SankeyChart ref={ref} options={merged} style={{ width: "100%", height: "100%" }} />
        </div>
    );
});

export const TreemapView = forwardRef<TreemapHandle | null, WrapProps<TreemapOptions>>(function TreemapView({ options, height, className, style }, ref) {
    const theme = useOrtexChartTheme();
    const ready = useReady();
    const merged = useMemo(() => ({ ...options, theme }), [options, theme]);
    if (!ready) return <div className={className} style={sized(height, style)} />;
    return (
        <div data-ortex-chart="" className={className} style={sized(height, style)}>
            <Treemap ref={ref} options={merged} style={{ width: "100%", height: "100%" }} />
        </div>
    );
});

export const HeatmapView = forwardRef<HeatmapHandle | null, WrapProps<HeatmapOptions>>(function HeatmapView({ options, height, className, style }, ref) {
    const theme = useOrtexChartTheme();
    const ready = useReady();
    const merged = useMemo(() => ({ ...options, theme }), [options, theme]);
    if (!ready) return <div className={className} style={sized(height, style)} />;
    return (
        <div data-ortex-chart="" className={className} style={sized(height, style)}>
            <Heatmap ref={ref} options={merged} style={{ width: "100%", height: "100%" }} />
        </div>
    );
});

export interface SparkProps {
    data: SparklineData;
    options?: Partial<SparklineOptions>;
    width?: number;
    height?: number;
    className?: string;
    style?: CSSProperties;
}

/** A sparkline in the app palette: ORTEX green line, positive/negative when colouring by trend. */
export const Spark = forwardRef<SparklineHandle | null, SparkProps>(function Spark({ data, options, width, height, className, style }, ref) {
    const theme = useOrtexChartTheme();
    const ready = useReady();
    const merged = useMemo<Partial<SparklineOptions>>(() => ({ color: theme.line, positiveColor: theme.up, negativeColor: theme.down, background: "transparent", ...options }), [options, theme]);
    if (!ready) return <span className={className} style={{ display: "inline-block", width, height, ...style }} />;
    return <Sparkline ref={ref} data={data} options={merged} width={width} height={height} className={className} style={style} />;
});
